import { LockRedSvg, LockSvg } from 'assets/svg-icon';
import React, { MouseEvent, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { blockChannelsRequest } from 'redux/reducers/channels/reducer';
import { blockUserRequest } from 'redux/reducers/users/reducer';
import { ELinksName } from 'services/constants/list-links-sidebar';
import { ETabsUsers, ETabsUsersDetails } from 'services/constants/lists-table';
import { Avatar } from 'ui/Avatar/Avatar';
import { PopUpAsk } from 'ui/PopUpAsk/PopUpAsk';

export interface UserInfoCardProps {
  id?: number; // U1 / внутрішній код
  handle: string; // @user123
  email?: string; // user123@email.com
  category?: string; // user123@email.com
  link?: string; // https://ello.co/channel/user123
  hash?: string; // https://ello.co/channel/user123
  userId: string | number; // 12345
  rating?: { value: number; max: number }; // { value: 2.3, max: 5 }
  joinYear: number; // 2025
  warnings: number; // 3
  tempBlocks: number; // 2
  title?: string; // заголовок, за замовчуванням — "User Information"
  className?: string;
  user?: boolean; // чи це картка користувача (інакше — каналу)
}

export const UserInfoCard: React.FC<UserInfoCardProps> = ({
  id,
  handle,
  email,
  category,
  hash,
  link,
  userId,
  rating = { value: 0, max: 5 },
  joinYear,
  warnings,
  tempBlocks,
  title = 'User Information',
  className,
  user,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const [username, setUsername] = useState<string>('');

  const onBlock = (e?: MouseEvent) => {
    const { id } = e?.currentTarget as HTMLDivElement;
    setUsername(id);
  };

  const onRemoveUser = () => {
    user
      ? dispatch(
          blockUserRequest({
            paramsApi: {
              [ETabsUsers.DELETE_USERS === params.userId ? 'recover' : 'block']:
                ETabsUsers.DELETE_USERS === params.userId
                  ? true
                  : params.userId?.includes('blocked')
                  ? false
                  : true,
              reasons: '',
              user_id: Number(userId),
            },
            navigate,
          }),
        )
      : dispatch(
          blockChannelsRequest({
            paramsApi: {
              deactivate: params.userId?.includes('blocked') ? false : true,
              channel_id: Number(userId),
            },
            navigate,
            group: false,
          }),
        );
  };

  return (
    <section
      className={`user-info-card ${className ?? ''}`}
      aria-label="User information"
      role="group"
    >
      <header className="user-info-card__header">
        <div className="user-info-card__title">
          {user ? (
            <span className="user-info-card__title-icon" aria-hidden>
              👤
            </span>
          ) : (
            <span className="user-info-card__title-icon" aria-hidden>
              {hash ? <Avatar hash={hash} /> : '📺'}
            </span>
          )}
          <h3 className="user-info-card__title-text">{title}</h3>
        </div>
      </header>

      <div className="user-info-card__grid">
        <ul className="user-info-card__list" aria-label="Basic data">
          <li className="user-info-card__row">
            <span className="user-info-card__key">{user ? 'Username' : 'Channel name'}</span>
            <span className="user-info-card__val">{handle}</span>
          </li>
          {user && (
            <li className="user-info-card__row">
              <span className="user-info-card__key">Email</span>
              <span className="user-info-card__val">{email}</span>
            </li>
          )}

          {!user && (
            <li className="user-info-card__row">
              <span className="user-info-card__key">Category</span>
              <span className="user-info-card__val">{category}</span>
            </li>
          )}

          {!user && (
            <li className="user-info-card__row">
              <span className="user-info-card__key">Link</span>
              <a href={link} className="user-info-card__val">
                {link}
              </a>
            </li>
          )}
          <li className="user-info-card__row">
            <span className="user-info-card__key">ID</span>
            <span className="user-info-card__val">
              {userId}
              {/* <span className="user-info-card__muted">
								, Rating: {rating.value}/{rating.max}
							</span> */}
            </span>
          </li>
        </ul>

        {/* right side */}
        <ul className="user-info-card__list" aria-label="User stats">
          <li className="user-info-card__row">
            <span className="user-info-card__icon" aria-hidden>
              🗓️
            </span>
            <span className="user-info-card__val">Member since {joinYear}</span>
          </li>
          <li className="user-info-card__row user-info-card__row--width">
            {user ? (
              <div className="user-info-card__wrap-btn">
                <Link
                  to={`${ELinksName.USER_DETAILS}/${userId}/${'registered'}/${String(
                    ETabsUsersDetails.USER_DATA,
                  )}`}
                  className="view-details"
                >
                  View user details
                </Link>
                <div
                  onClick={(event: MouseEvent) => onBlock(event)}
                  className="user-data__toggle-wrapper right pointer blocked"
                  id={String(handle)}
                >
                  {id ? <LockSvg /> : <LockRedSvg />}
                  <span className={`user-data__toggle-text ${id ? 'green' : 'red'}`}>
                    {id ? 'Unblock' : 'Block'}
                  </span>
                </div>
              </div>
            ) : (
              <div className="user-info-card__wrap-btn">
                <Link
                  to={`${ELinksName.CHANNEL_DETAIL_USER}/${userId}/${'all-channels'}`}
                  className="view-details"
                >
                  View channel details
                </Link>
                <div
                  onClick={(event: MouseEvent) => onBlock(event)}
                  className="user-data__toggle-wrapper right pointer blocked"
                  id={String(handle)}
                >
                  {id ? <LockSvg /> : <LockRedSvg />}
                  <span className={`user-data__toggle-text ${id ? 'green' : 'red'}`}>
                    {id ? 'Unblock' : 'Block'}
                  </span>
                </div>
              </div>
            )}
          </li>
        </ul>
      </div>
      {username && (
        <PopUpAsk
          setOpen={setUsername}
          title={`Are you sure you want to ${username} this ${user ? 'user' : 'channel'}?`}
          btnNameOne="Yes, I’m sure"
          btnNameTwo="Cancel"
          onClick={onRemoveUser}
        />
      )}
    </section>
  );
};
